import { useNavigate } from "react-router-dom";
import { Building2, Mail, Phone, MapPin } from "lucide-react";

function CompanyDetails() {
  const navigate = useNavigate();
  const handleContact = () => navigate("/contactUs");

  return (
    <div>
      {/* Hero section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1>Company Details</h1>
          <p>
            AirArrange is an Australian company operating a digital marketplace
            for workspace bookings. Below are our official company details and
            contact information.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-5xl mx-auto px-6 py-16 space-y-12">
        {/* Company Information Card */}
        <div className="bg-gray-50 border rounded-lg p-8">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="w-8 h-8 text-[var(--secondary)]" />
            <h2 className="text-2xl font-semibold">
              Official Company Information
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">
                Company Name
              </h3>
              <p className="text-gray-600">AirArrange Pty Ltd</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">
                Australian Business Number (ABN)
              </h3>
              <p className="text-gray-600">XX XXX XXX XXX</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">
                Australian Company Number (ACN)
              </h3>
              <p className="text-gray-600">XXX XXX XXX</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">
                Entity Type
              </h3>
              <p className="text-gray-600">Australian Proprietary Company</p>
            </div>
          </div>
        </div>

        {/* Registered Office */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-6 h-6 text-[var(--secondary)]" />
            <h2 className="text-2xl font-semibold">Registered Office</h2>
          </div>
          <div className="bg-white border rounded-lg p-6">
            <p className="text-gray-700 leading-relaxed">
              [Street Address]
              <br />
              [Suburb], [State] [Postcode]
              <br />
              Australia
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Email */}
            <div className="bg-white border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-5 h-5 text-[var(--secondary)]" />
                <h3 className="font-semibold text-gray-800">Email</h3>
              </div>
              <a
                href="mailto:info@airarrange.com"
                className="text-[var(--secondary)] hover:underline"
              >
                info@airarrange.com
              </a>
            </div>

            {/* Phone */}
            <div className="bg-white border rounded-lg p-6">
              <div className="flex items-center gap-3 mb-3">
                <Phone className="w-5 h-5 text-[var(--secondary)]" />
                <h3 className="font-semibold text-gray-800">Phone</h3>
              </div>
              <a
                href="tel:+61XXXXXXXXX"
                className="text-[var(--secondary)] hover:underline"
              >
                +61 X XXXX XXXX
              </a>
            </div>
          </div>
        </div>

        {/* Business Operations */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Business Operations</h2>
          <div className="bg-gray-50 border rounded-lg p-6">
            <p className="text-gray-700 leading-relaxed mb-4">
              AirArrange operates a digital marketplace platform that connects
              workspace providers with individuals and businesses seeking
              flexible workspace solutions.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We are not a real estate agent, broker, or property manager.
              AirArrange facilitates connections and transactions between
              independent parties and does not own, lease, or manage any
              workspace listed on the platform.
            </p>
          </div>
        </div>

        {/* Regulatory Information */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            Regulatory Information
          </h2>
          <ul className="list-disc pl-6 text-gray-600 space-y-2">
            <li>
              Registered with the Australian Securities and Investments
              Commission (ASIC)
            </li>
            <li>
              Compliant with the Privacy Act 1988 (Cth) and Australian Privacy
              Principles
            </li>
            <li>
              All payment processing is handled securely through Stripe in
              accordance with PCI DSS standards
            </li>
            <li>
              Subject to Australian Consumer Law and relevant state and
              territory legislation
            </li>
          </ul>
        </div>

        {/* Dispute Resolution */}
        <div>
          <h2 className="text-2xl font-semibold mb-4">Dispute Resolution</h2>
          <p className="text-gray-600">
            AirArrange is committed to resolving disputes fairly and
            efficiently. If you have a complaint or concern, please contact us
            first. Unresolved disputes may be referred to the appropriate
            dispute resolution body or the courts of New South Wales, Australia.
          </p>
        </div>

        {/* Contact */}
        <div className="border-t pt-8">
          <h2 className="text-2xl font-semibold mb-4">Get in Touch</h2>
          <p className="text-gray-600 mb-4">
            For general enquiries, support, or to report an issue, please
            contact us.
          </p>
          <button className="secondary-button" onClick={handleContact}>
            Contact Us
          </button>
        </div>
      </section>
    </div>
  );
}

export default CompanyDetails;
